import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { DiscountService } from 'src/app/services/discount.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { InfoCardComponent } from '../../shared/info-card/info-card.component';
import { RULE_TYPE_LABELS, DISCOUNT_MODE_LABELS, SCOPE_LABELS } from 'src/app/interfaces/discount';

@Component({
  selector: 'app-discounts-list',
  templateUrl: './discounts-list.component.html',
  styleUrls: ['./discounts-list.component.scss']
})
export class DiscountsListComponent implements OnInit {

  business: any;
  profile: any;
  discounts: any[] = [];
  loaded: boolean = false;
  loadingPage: boolean = true;

  ruleTypeFilter: string = '';
  activeFilter: string = '';
  searchText: string = '';
  isSuperAdmin: boolean = false;

  typeDropdownOpen: boolean = false;
  statusDropdownOpen: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-group')) {
      this.typeDropdownOpen = false;
      this.statusDropdownOpen = false;
    }
  }

  private searchTerms = new Subject<string>();

  displayedColumns: string[] = ['actions', 'name', 'rule_type', 'value', 'dates', 'status'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  @ViewChild(MatSort) sort: MatSort = new MatSort();

  constructor(
    private discountService: DiscountService,
    private auth: AuthenticationService,
    private apiBusiness: ApiBusinessService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.profile = this.auth.getProfile();
    const roles = this.profile?.roles?.map((r: any) => r.name) || [];
    this.isSuperAdmin = roles.includes('superadmin');
  }

  ngAfterViewInit() {
    this.apiBusiness.changed.subscribe({
      next: (value) => {
        this.business = JSON.parse(localStorage.getItem('business') || '{}');
        if (value) {
          this.loadDiscounts();
        }
      }
    });

    this.searchTerms.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchText = term;
      this.loadDiscounts();
    });
  }

  onSearch(value: string) {
    this.searchTerms.next(value);
  }

  loadDiscounts() {
    this.loadingPage = true;
    const companyId = this.business?.id;
    const search = this.searchText || undefined;
    const obs = this.isSuperAdmin && !companyId
      ? this.discountService.getDiscounts(undefined, search)
      : this.discountService.getDiscountsByCompany(companyId, search);

    obs.subscribe({
      next: (res) => {
        let data = (res.discounts || []).filter((d: any) => d.rule_type !== 'pass');
        // Client-side filters
        if (this.ruleTypeFilter) {
          data = data.filter((d: any) => d.rule_type === this.ruleTypeFilter);
        }
        if (this.activeFilter !== '') {
          const isActive = this.activeFilter === 'true';
          data = data.filter((d: any) => d.active === isActive);
        }
        this.discounts = data;
        this.dataSource = new MatTableDataSource(this.discounts);
        this.dataSource.sort = this.sort;
        this.loaded = true;
        this.loadingPage = false;
      },
      error: () => {
        this.discounts = [];
        this.dataSource = new MatTableDataSource<any>([]);
        this.loaded = true;
        this.loadingPage = false;
      }
    });
  }

  onFilterChange() {
    this.loadDiscounts();
  }

  clearFilters() {
    this.ruleTypeFilter = '';
    this.activeFilter = '';
    this.searchText = '';
    this.loadDiscounts();
  }

  getRuleTypeLabel(type: string): string {
    return RULE_TYPE_LABELS[type] || type;
  }

  getDiscountModeLabel(mode: string): string {
    return DISCOUNT_MODE_LABELS[mode] || mode;
  }

  getScopeLabel(scope: string): string {
    return SCOPE_LABELS[scope] || scope;
  }

  getValueDisplay(discount: any): string {
    const base = discount.discount_mode === 'percentage'
      ? `${discount.discount_value}%`
      : `${discount.discount_value}€`;
    if (discount.discount_mode === 'percentage' && discount.max_discount_amount != null) {
      const max = parseFloat(discount.max_discount_amount).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `${base} (máx. ${max}€)`;
    }
    return base;
  }

  getDatesDisplay(discount: any): string {
    if (!discount.starts_at && !discount.ends_at) return 'Sin límite';
    const start = discount.starts_at ? new Date(discount.starts_at).toLocaleDateString('es-ES') : '...';
    const end = discount.ends_at ? new Date(discount.ends_at).toLocaleDateString('es-ES') : '...';
    return `${start} - ${end}`;
  }

  openCreate() {
    this.router.navigate(['/panel-control/discounts/create']);
  }

  openEdit(discount: any) {
    this.router.navigate(['/panel-control/discounts/edit', discount.id]);
  }

  toggleActive(discount: any) {
    const action = discount.active ? 'desactivar' : 'activar';
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { icon: true, text: `¿Estás seguro de que quieres ${action} este descuento?`, btnCancel: true }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.discountService.toggleDiscountActive(discount.id).subscribe({
          next: () => {
            this.openInfoCard(`Descuento ${action === 'activar' ? 'activado' : 'desactivado'} correctamente`);
            this.loadDiscounts();
          },
          error: () => {
            this.openInfoCard('Error al cambiar el estado del descuento');
          }
        });
      }
    });
  }

  deleteDiscount(discount: any) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      disableClose: true,
      data: { icon: true, text: '¿Estás seguro de que quieres eliminar este descuento? Esta acción no podrá deshacerse.', btnCancel: true, warning: true }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.discountService.deleteDiscount(discount.id).subscribe({
          next: () => {
            this.openInfoCard('Descuento eliminado correctamente');
            this.loadDiscounts();
          },
          error: () => {
            this.openInfoCard('Error al eliminar el descuento');
          }
        });
      }
    });
  }

  private openInfoCard(msg: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text: msg }
    });
  }
}
